import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import metadataApi from '../../api/metadata';
import astroApi from '../../api/astro';

const COLUMN_GROUP_SUFFIX_PATTERN = /^[A-Za-z0-9]{1,8}$/;

const columnSignature = (column) => `${column.data_type}|${column.is_nullable ? 'nullable' : 'required'}`;

const formatColumnLine = (column) => {
  const nullability = column.is_nullable ? 'nullable' : 'not null';
  return `${column.name}: ${column.data_type} (${nullability})`;
};

const compactColumns = (columns) => {
  const groupedColumns = new Map();
  const standaloneColumns = [];

  columns.forEach((column) => {
    const parts = column.name.split('_');
    const suffix = parts[parts.length - 1];

    if (parts.length >= 2 && COLUMN_GROUP_SUFFIX_PATTERN.test(suffix)) {
      const prefix = parts.slice(0, -1).join('_');
      const key = `${prefix}|${columnSignature(column)}`;
      const existingGroup = groupedColumns.get(key);

      if (existingGroup) {
        existingGroup.suffixes.push(suffix);
      } else {
        groupedColumns.set(key, {
          prefix,
          data_type: column.data_type,
          is_nullable: column.is_nullable,
          suffixes: [suffix]
        });
      }
      return;
    }

    standaloneColumns.push(column);
  });

  const compacted = [];

  groupedColumns.forEach((group) => {
    if (group.suffixes.length < 3) {
      group.suffixes.forEach((suffix) => {
        compacted.push(formatColumnLine({
          name: `${group.prefix}_${suffix}`,
          data_type: group.data_type,
          is_nullable: group.is_nullable
        }));
      });
      return;
    }

    const nullability = group.is_nullable ? 'nullable' : 'not null';
    const orderedSuffixes = [...group.suffixes].sort((left, right) => left.localeCompare(right));
    compacted.push(`${group.prefix}_[${orderedSuffixes.join(', ')}]: ${group.data_type} (${nullability})`);
  });

  standaloneColumns.forEach((column) => {
    compacted.push(formatColumnLine(column));
  });

  return compacted.sort((left, right) => left.localeCompare(right));
};

const DatabaseExplorer = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [schemas, setSchemas] = useState([]);
  const [expandedSchemas, setExpandedSchemas] = useState({});
  const [expandedTables, setExpandedTables] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [tablesBySchema, setTablesBySchema] = useState({});
  const [columnsByTable, setColumnsByTable] = useState({});
  const [loadingSchema, setLoadingSchema] = useState(null);
  const [loadingTable, setLoadingTable] = useState(null);
  const [astroTables, setAstroTables] = useState([]);
  const [astroTablesLoaded, setAstroTablesLoaded] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);
  const navigate = useNavigate();

  // Load list of schemas
  useEffect(() => {
    const fetchSchemas = async () => {
      try {
        setLoading(true);
        setError(null);
        const schemasList = await metadataApi.getSchemas();
        setSchemas(schemasList);
      } catch (err) {
        console.error('Failed to fetch schemas:', err);
        setError('Failed to load database schemas. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchSchemas();
  }, []);

  // Load astrotables only once when first schema is expanded
  const loadAstroTablesOnce = async () => {
    if (astroTablesLoaded) return;
    
    try {
      const astroTablesList = await astroApi.getAstroTables();
      console.log('Loaded astrotables:', astroTablesList);
      setAstroTables(astroTablesList);
      setAstroTablesLoaded(true);
    } catch (err) {
      console.error('Failed to fetch astrotables:', err);
      // Don't set error state as this is not critical for basic functionality
    }
  };

  // Function to load tables for a schema when expanded
  const loadTablesForSchema = async (schema) => {
    if (tablesBySchema[schema]) return; // Already loaded
    
    try {
      setLoadingSchema(schema);
      const tables = await metadataApi.getTables(schema);
      setTablesBySchema(prev => ({
        ...prev,
        [schema]: tables
      }));
    } catch (err) {
      console.error(`Failed to load tables for schema ${schema}:`, err);
      setTablesBySchema(prev => ({
        ...prev,
        [schema]: []
      }));
    } finally {
      setLoadingSchema(null);
    }
  };

  // Function to load columns for a table when expanded
  const loadColumnsForTable = async (schema, table) => {
    const tableKey = `${schema}.${table}`;
    if (columnsByTable[tableKey]) return; // Already loaded
    
    try {
      setLoadingTable(tableKey);
      const columns = await metadataApi.getColumns(schema, table);
      setColumnsByTable(prev => ({
        ...prev,
        [tableKey]: columns
      }));
    } catch (err) {
      console.error(`Failed to load columns for ${schema}.${table}:`, err);
      setColumnsByTable(prev => ({
        ...prev,
        [tableKey]: []
      }));
    } finally {
      setLoadingTable(null);
    }
  };

  const toggleSchemaExpanded = async (schema) => {
    const newExpandedState = !expandedSchemas[schema];
    
    setExpandedSchemas(prev => ({
      ...prev,
      [schema]: newExpandedState
    }));
    
    if (newExpandedState) {
      await loadAstroTablesOnce(); // Load astrotables only once when first schema is expanded
      await loadTablesForSchema(schema);
    }
  };

  const toggleTableExpanded = async (schema, table) => {
    const tableKey = `${schema}.${table}`;
    const newExpandedState = !expandedTables[tableKey];
    
    setExpandedTables(prev => ({
      ...prev,
      [tableKey]: newExpandedState
    }));
    
    if (newExpandedState) {
      await loadColumnsForTable(schema, table);
    }
  };

  const handleGenerateQuery = (e, schema, table) => {
    e.stopPropagation(); // Prevent the click from toggling the table
    navigate('/query', { 
      state: { 
        prefilledQuery: `SELECT TOP 100 * FROM ${schema}.${table}`
      }
    });
  };

  const writeClipboard = async (text, key) => {
    await navigator.clipboard.writeText(text);
    setCopiedKey(key);
    window.setTimeout(() => {
      setCopiedKey((currentKey) => (currentKey === key ? null : currentKey));
    }, 2000);
  };

  const getColumnsForTable = async (schemaName, tableName) => {
    const tableKey = `${schemaName}.${tableName}`;
    const cachedColumns = columnsByTable[tableKey];

    if (cachedColumns) {
      return cachedColumns;
    }

    const fetchedColumns = await metadataApi.getColumns(schemaName, tableName);
    setColumnsByTable((prev) => ({
      ...prev,
      [tableKey]: fetchedColumns
    }));
    return fetchedColumns;
  };

  const copyTableSummary = async (e, schemaName, tableName) => {
    e.stopPropagation();

    try {
      const columns = await getColumnsForTable(schemaName, tableName);
      const compactedColumns = compactColumns(columns);
      const payload = [
        `table ${schemaName}.${tableName}`,
        `columns (${columns.length})`,
        ...compactedColumns.map((line) => `- ${line}`)
      ].join('\n');

      await writeClipboard(payload, `table:${schemaName}.${tableName}`);
    } catch (err) {
      console.error(`Failed to copy table summary for ${schemaName}.${tableName}:`, err);
    }
  };

  const copySchemaSummary = async (e, schemaName) => {
    e.stopPropagation();

    try {
      let schemaTables = tablesBySchema[schemaName];
      if (!schemaTables) {
        schemaTables = await metadataApi.getTables(schemaName);
        setTablesBySchema((prev) => ({
          ...prev,
          [schemaName]: schemaTables
        }));
      }

      const tablesWithColumns = await Promise.all(
        schemaTables.map(async (tableName) => ({
          tableName,
          columns: await getColumnsForTable(schemaName, tableName)
        }))
      );

      const payload = [
        `schema ${schemaName}`,
        `tables (${schemaTables.length})`,
        ...tablesWithColumns.flatMap(({ tableName, columns }) => [
          '',
          `table ${schemaName}.${tableName}`,
          ...compactColumns(columns).map((line) => `- ${line}`)
        ])
      ].join('\n');

      await writeClipboard(payload, `schema:${schemaName}`);
    } catch (err) {
      console.error(`Failed to copy schema summary for ${schemaName}:`, err);
    }
  };

  // Filter database objects based on search query
  const filterSchemas = () => {
    if (!searchQuery.trim()) {
      return schemas;
    }
    
    const query = searchQuery.toLowerCase();
    return schemas.filter(schema => {
      // Check if schema name matches
      if (schema.toLowerCase().includes(query)) {
        return true;
      }
      
      // Check if any tables in this schema match
      const schemaTables = tablesBySchema[schema] || [];
      return schemaTables.some(table => {
        // Check table name
        if (table.toLowerCase().includes(query)) {
          return true;
        }
        
        // Check columns in this table if they're loaded
        const tableKey = `${schema}.${table}`;
        const columns = columnsByTable[tableKey] || [];
        return columns.some(col => 
          col.name.toLowerCase().includes(query) || 
          col.data_type.toLowerCase().includes(query)
        );
      });
    });
  };

  // Helper function to find astrotable with documentation_route
  const findAstroTableWithDocs = (schemaName, tableName) => {
    if (!astroTables || astroTables.length === 0) {
      return null;
    }
    
    const result = astroTables.find(astroTable => {
      const matchesSchema = astroTable.schema_name === schemaName;
      const matchesTable = astroTable.table_name === tableName;
      const hasDocRoute = astroTable.documentation_route && astroTable.documentation_route.trim() !== '';
      
      return matchesSchema && matchesTable && hasDocRoute;
    });
    
    return result;
  };

  const filteredSchemas = filterSchemas();

  if (loading) {
    return (
      <div className="p-8 bg-ui-light-card dark:bg-ui-dark-card rounded-xl shadow-lg transition-colors min-h-[600px]">
        <div className="flex flex-col items-center justify-center space-y-4 h-full">
          <div className="relative">
            <div className="w-12 h-12 border-4 border-primary-200 dark:border-primary-800 rounded-full"></div>
            <div className="absolute top-0 left-0 w-12 h-12 border-4 border-transparent border-t-primary-600 dark:border-t-primary-400 rounded-full animate-spin"></div>
          </div>
          <div className="text-center">
            <p className="text-lg font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">
              Loading Database
            </p>
            <p className="text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary mt-1">
              Discovering schemas and structure...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 bg-ui-light-card dark:bg-ui-dark-card rounded-xl shadow-lg transition-colors min-h-[600px]">
        <div className="text-center h-full flex flex-col justify-center">
          <div className="mx-auto w-16 h-16 bg-status-error-light-bg dark:bg-status-error-dark-bg rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-status-error-light-text dark:text-status-error-dark-text" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-ui-light-text-primary dark:text-ui-dark-text-primary mb-2">
            Connection Failed
          </h3>
          <p className="text-sm text-ui-light-text-secondary dark:text-ui-dark-text-secondary mb-6">
            {error}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 dark:bg-primary-700 dark:hover:bg-primary-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200 transform hover:scale-[1.02]"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!filteredSchemas || filteredSchemas.length === 0) {
    return (
      <div className="p-6 bg-ui-light-card dark:bg-ui-dark-card rounded-xl shadow-lg transition-colors">
        <div className="mb-6">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search schemas, tables, or columns..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 text-sm text-ui-light-text-primary dark:text-ui-dark-text-primary bg-ui-light-background dark:bg-ui-dark-background border border-ui-light-border dark:border-ui-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
            />
          </div>
        </div>
        
        <div className="text-center py-12">
          <div className="mx-auto w-16 h-16 bg-ui-light-hover dark:bg-ui-dark-hover rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary mb-2">
            {searchQuery ? 'No Results Found' : 'No Schemas Available'}
          </h3>
          <p className="text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
            {searchQuery ? 'Try adjusting your search terms' : 'No database schemas are currently accessible'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-ui-light-card dark:bg-ui-dark-card rounded-xl shadow-lg transition-colors">
      {/* Legend */}
      <div className="mb-6 p-4 bg-ui-light-background dark:bg-ui-dark-background border border-ui-light-border dark:border-ui-dark-border rounded-lg">
        <h3 className="text-sm font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary mb-3">Legend</h3>
        <div className="flex items-center space-x-6 text-xs text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
          <div className="flex items-center space-x-2">
            <svg className="w-4 h-4 text-ui-light-text-primary dark:text-ui-dark-text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            <span>Schema</span>
          </div>
          <div className="flex items-center space-x-2">
            <svg className="w-4 h-4 text-ui-light-text-primary dark:text-ui-dark-text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            <span>Table</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-ui-light-text-primary dark:bg-ui-dark-text-primary rounded-full"></div>
            <span>Column</span>
          </div>
        </div>
      </div>

      {/* Enhanced Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            type="text"
            placeholder="Search schemas, tables, or columns..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 text-sm text-ui-light-text-primary dark:text-ui-dark-text-primary bg-ui-light-background dark:bg-ui-dark-background border border-ui-light-border dark:border-ui-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute inset-y-0 right-0 pr-4 flex items-center"
            >
              <svg className="h-5 w-5 text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary hover:text-ui-light-text-primary dark:hover:text-ui-dark-text-primary transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        {searchQuery && (
          <p className="mt-2 text-xs text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
            Showing results for "{searchQuery}"
          </p>
        )}
      </div>
      
      {/* Enhanced Tree Structure */}
      <div className="border border-ui-light-border dark:border-ui-dark-border rounded-xl overflow-hidden bg-ui-light-background dark:bg-ui-dark-background">
        <div className="overflow-auto max-h-[calc(100vh-280px)]">
          {filteredSchemas.map((schemaName, schemaIndex) => {
            const isExpanded = expandedSchemas[schemaName];
            const schemaTables = tablesBySchema[schemaName] || [];
            const isLoading = loadingSchema === schemaName;
            
            return (
              <div 
                key={schemaName} 
                className={`${schemaIndex !== filteredSchemas.length - 1 ? 'border-b border-ui-light-border dark:border-ui-dark-border' : ''}`}
              >
                {/* Schema Header */}
                <div 
                  className="flex items-center justify-between px-6 py-4 hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover cursor-pointer transition-all duration-150 group"
                  onClick={() => toggleSchemaExpanded(schemaName)}
                >
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      {isExpanded ? (
                        <svg className="w-5 h-5 text-primary-600 dark:text-primary-400 transition-transform duration-150" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5 text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-all duration-150" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-ui-light-hover dark:bg-ui-dark-hover rounded-lg flex items-center justify-center flex-shrink-0">
                        <svg className="w-4 h-4 text-ui-light-text-primary dark:text-ui-dark-text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-150">
                          {schemaName}
                        </h3>
                        <p className="text-xs text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                          Database Schema
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    {isLoading ? (
                      <div className="w-5 h-5 border-2 border-primary-200 dark:border-primary-800 border-t-primary-600 dark:border-t-primary-400 rounded-full animate-spin"></div>
                    ) : (
                      tablesBySchema[schemaName] && (
                        <span className="px-2 py-1 text-xs font-medium text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary bg-ui-light-hover dark:bg-ui-dark-hover rounded-full">
                          {schemaTables.length} {schemaTables.length === 1 ? 'table' : 'tables'}
                        </span>
                      )
                    )}
                    <button
                      type="button"
                      onClick={(e) => copySchemaSummary(e, schemaName)}
                      className="px-3 py-1.5 text-xs font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary bg-ui-light-hover dark:bg-ui-dark-hover hover:bg-ui-light-card dark:hover:bg-ui-dark-card rounded-md transition-all duration-150"
                      title="Copy compact schema summary"
                    >
                      {copiedKey === `schema:${schemaName}` ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                </div>
                
                {/* Expanded Schema Content */}
                {isExpanded && (
                  <div className="bg-ui-light-card dark:bg-ui-dark-card border-t border-ui-light-border dark:border-ui-dark-border">
                    {isLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="flex items-center space-x-3">
                          <div className="w-5 h-5 border-2 border-primary-200 dark:border-primary-800 border-t-primary-600 dark:border-t-primary-400 rounded-full animate-spin"></div>
                          <span className="text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">Loading tables...</span>
                        </div>
                      </div>
                    ) : schemaTables.length === 0 ? (
                      <div className="px-6 py-8 text-center">
                        <div className="w-12 h-12 mx-auto bg-ui-light-hover dark:bg-ui-dark-hover rounded-lg flex items-center justify-center mb-3">
                          <svg className="w-6 h-6 text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                          </svg>
                        </div>
                        <p className="text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                          No tables found in this schema
                        </p>
                      </div>
                    ) : (
                      <div className="divide-y divide-ui-light-border dark:divide-ui-dark-border">
                        {schemaTables.map(tableName => {
                          const tableKey = `${schemaName}.${tableName}`;
                          const isTableExpanded = expandedTables[tableKey];
                          const isTableLoading = loadingTable === tableKey;
                          const tableColumns = columnsByTable[tableKey] || [];
                          const hasDocumentation = findAstroTableWithDocs(schemaName, tableName);
                          
                          return (
                            <div key={tableKey}>
                              {/* Table Header */}
                              <div 
                                className="flex items-center justify-between px-6 py-4 hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover cursor-pointer transition-all duration-150 group"
                                onClick={() => toggleTableExpanded(schemaName, tableName)}
                              >
                                <div className="flex items-center space-x-3">
                                  <div className="flex-shrink-0 pl-4">
                                    {isTableExpanded ? (
                                      <svg className="w-4 h-4 text-primary-600 dark:text-primary-400 transition-transform duration-150" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                      </svg>
                                    ) : (
                                      <svg className="w-4 h-4 text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-all duration-150" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                      </svg>
                                    )}
                                  </div>
                                  <div className="flex items-center space-x-3">
                                    <div className="w-7 h-7 bg-ui-light-hover dark:bg-ui-dark-hover rounded-md flex items-center justify-center flex-shrink-0">
                                      <svg className="w-3.5 h-3.5 text-ui-light-text-primary dark:text-ui-dark-text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                      </svg>
                                    </div>
                                    <div>
                                      <h4 className="font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-150">
                                        {tableName}
                                      </h4>
                                      {hasDocumentation && (
                                        <p className="text-xs text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                                          Documentation available
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                
                                <div className="flex items-center space-x-2">
                                  {isTableLoading ? (
                                    <div className="w-4 h-4 border-2 border-primary-200 dark:border-primary-800 border-t-primary-600 dark:border-t-primary-400 rounded-full animate-spin"></div>
                                  ) : (
                                    columnsByTable[tableKey] && (
                                      <span className="px-2 py-1 text-xs font-medium text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary bg-ui-light-background dark:bg-ui-dark-background rounded-full">
                                        {tableColumns.length} {tableColumns.length === 1 ? 'column' : 'columns'}
                                      </span>
                                    )
                                  )}
                                  
                                  {/* Action Buttons */}
                                  <div className="flex items-center space-x-2">
                                    <button
                                      onClick={(e) => copyTableSummary(e, schemaName, tableName)}
                                      className="px-3 py-1.5 text-xs font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary bg-ui-light-background dark:bg-ui-dark-background hover:bg-ui-light-card dark:hover:bg-ui-dark-card rounded-md transition-all duration-150 transform hover:scale-105 opacity-0 group-hover:opacity-100"
                                      title="Copy compact table summary"
                                    >
                                      {copiedKey === `table:${schemaName}.${tableName}` ? 'Copied' : 'Copy'}
                                    </button>
                                    <button
                                      onClick={(e) => handleGenerateQuery(e, schemaName, tableName)}
                                      className="px-3 py-1.5 text-xs font-medium text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-800/30 rounded-md transition-all duration-150 transform hover:scale-105 opacity-0 group-hover:opacity-100"
                                      title="Generate query"
                                    >
                                      <svg className="w-3.5 h-3.5 mr-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3" />
                                      </svg>
                                      Query
                                    </button>
                                    
                                    {hasDocumentation && (
                                      <Link
                                        to={hasDocumentation.documentation_route}
                                        onClick={(e) => e.stopPropagation()}
                                        className="flex items-center px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 rounded-md transition-all duration-150 transform hover:scale-105 shadow-sm"
                                        title="View documentation"
                                      >
                                        <svg className="w-3.5 h-3.5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        Docs
                                      </Link>
                                    )}
                                  </div>
                                </div>
                              </div>
                              
                              {/* Expanded Table Content */}
                              {isTableExpanded && (
                                <div className="pl-6 pr-6 pb-4 bg-ui-light-hover dark:bg-ui-dark-hover">
                                  {isTableLoading ? (
                                    <div className="flex items-center justify-center py-6">
                                      <div className="flex items-center space-x-3">
                                        <div className="w-4 h-4 border-2 border-primary-200 dark:border-primary-800 border-t-primary-600 dark:border-t-primary-400 rounded-full animate-spin"></div>
                                        <span className="text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">Loading columns...</span>
                                      </div>
                                    </div>
                                  ) : tableColumns.length === 0 ? (
                                    <div className="py-6 text-center">
                                      <div className="w-10 h-10 mx-auto bg-ui-light-background dark:bg-ui-dark-background rounded-lg flex items-center justify-center mb-3">
                                        <svg className="w-5 h-5 text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                        </svg>
                                      </div>
                                      <p className="text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                                        No columns found in this table
                                      </p>
                                    </div>
                                  ) : (
                                    <div className="ml-4">
                                      {/* Enhanced Columns Table */}
                                      <div className="bg-ui-light-background dark:bg-ui-dark-background rounded-lg border border-ui-light-border dark:border-ui-dark-border overflow-hidden">
                                        <div className="px-4 py-3 bg-ui-light-card dark:bg-ui-dark-card border-b border-ui-light-border dark:border-ui-dark-border">
                                          <div className="flex items-center space-x-2">
                                            <svg className="w-4 h-4 text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2H9z" />
                                            </svg>
                                            <h5 className="text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
                                              Table Structure
                                            </h5>
                                            <span className="px-2 py-0.5 text-xs font-medium text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary bg-ui-light-hover dark:bg-ui-dark-hover rounded-full">
                                              {tableColumns.length} columns
                                            </span>
                                          </div>
                                        </div>
                                        
                                        <div className="overflow-x-auto">
                                          <table className="min-w-full text-xs">
                                            <thead className="bg-ui-light-hover dark:bg-ui-dark-hover">
                                              <tr>
                                                <th className="px-4 py-3 text-left text-xs font-semibold text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase tracking-wider">
                                                  Column Name
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-semibold text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase tracking-wider">
                                                  Data Type
                                                </th>
                                                <th className="px-4 py-3 text-center text-xs font-semibold text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase tracking-wider">
                                                  Nullable
                                                </th>
                                              </tr>
                                            </thead>
                                            <tbody className="divide-y divide-ui-light-border dark:divide-ui-dark-border">
                                              {tableColumns.map((column, index) => (
                                                <tr 
                                                  key={column.name} 
                                                  className={`${index % 2 === 0 ? 'bg-ui-light-background dark:bg-ui-dark-background' : 'bg-ui-light-card dark:bg-ui-dark-card'} hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover transition-colors duration-150`}
                                                >
                                                  <td className="px-4 py-3">
                                                    <div className="flex items-center space-x-2">
                                                      <div className="w-2 h-2 bg-ui-light-text-primary dark:bg-ui-dark-text-primary rounded-full flex-shrink-0"></div>
                                                      <span className="font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">
                                                        {column.name}
                                                      </span>
                                                    </div>
                                                  </td>
                                                  <td className="px-4 py-3">
                                                    <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                                                      {column.data_type}
                                                    </span>
                                                  </td>
                                                  <td className="px-4 py-3 text-center">
                                                    {column.is_nullable ? (
                                                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300">
                                                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                                        </svg>
                                                        Yes
                                                      </span>
                                                    ) : (
                                                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                                                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                        </svg>
                                                        No
                                                      </span>
                                                    )}
                                                  </td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Footer with helpful info */}
      {filteredSchemas.length > 0 && (
        <div className="mt-6 pt-4 border-t border-ui-light-border dark:border-ui-dark-border">
          <div className="flex items-center justify-between text-xs text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
            <div className="flex items-center space-x-4">
              <span>
                {filteredSchemas.length} {filteredSchemas.length === 1 ? 'schema' : 'schemas'}
                {searchQuery && ' matching your search'}
              </span>
              {Object.keys(expandedSchemas).filter(key => expandedSchemas[key]).length > 0 && (
                <span>
                  • {Object.keys(tablesBySchema).reduce((total, schema) => 
                    total + (tablesBySchema[schema]?.length || 0), 0
                  )} tables loaded
                </span>
              )}
            </div>
            <div className="flex items-center space-x-1">
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Click to expand • Hover for actions</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatabaseExplorer;
